import React from 'react';
import {
  Scan,
  UserCheck,
  PackageCheck,
  Calendar,
  CreditCard,
  DoorOpen,
  Users,
  ClipboardList,
  Scale,
  Shield,
  Briefcase,
  Truck,
  Wrench,
  Headphones,
  PiggyBank,
  UserCog
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { techSolutions, additionalServices } from '../mock';
import CTABanner from './CTABanner';

const iconMap = {
  Scan,
  UserCheck,
  PackageCheck,
  Calendar,
  CreditCard,
  DoorOpen,
  Users,
  ClipboardList,
  Scale,
  Shield,
  Briefcase,
  Truck,
  Wrench,
  Headphones,
  PiggyBank,
  UserCog
};

const TechSolutions = () => {
  return (
    <section id="tecnologia" className="py-24 bg-white relative overflow-hidden">
      {/* Building watermark background */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <svg className="absolute left-0 top-0 h-full" width="200" viewBox="0 0 200 600" xmlns="http://www.w3.org/2000/svg">
          <rect x="20" y="100" width="60" height="500" fill="#082E5C"/>
          <rect x="30" y="120" width="10" height="15" fill="#00D4D8"/>
          <rect x="50" y="120" width="10" height="15" fill="#00D4D8"/>
          <rect x="30" y="150" width="10" height="15" fill="#00D4D8"/>
          <rect x="50" y="150" width="10" height="15" fill="#00D4D8"/>
          <rect x="30" y="180" width="10" height="15" fill="#00D4D8"/>
          <rect x="50" y="180" width="10" height="15" fill="#00D4D8"/>
        </svg>
        <svg className="absolute right-0 top-0 h-full" width="250" viewBox="0 0 250 600" xmlns="http://www.w3.org/2000/svg">
          <rect x="170" y="50" width="70" height="550" fill="#082E5C"/>
          <rect x="180" y="70" width="12" height="18" fill="#FFDA0C"/>
          <rect x="198" y="70" width="12" height="18" fill="#FFDA0C"/>
          <rect x="180" y="100" width="12" height="18" fill="#FFDA0C"/>
          <rect x="198" y="100" width="12" height="18" fill="#FFDA0C"/>
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#00D4D8]/10 rounded-2xl mb-6">
            <Scan className="text-[#00D4D8]" size={32} />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#082E5C] mb-4">
            Tecnología e Innovación
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Automatizamos todos los procesos de tu comunidad con inteligencia artificial y soluciones digitales
          </p>
        </div>

        <Tabs defaultValue="services" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
            <TabsTrigger value="services" className="data-[state=active]:bg-[#00D4D8] data-[state=active]:text-white">
              Servicios Principales
            </TabsTrigger>
            <TabsTrigger value="tech" className="data-[state=active]:bg-[#00D4D8] data-[state=active]:text-white">
              Soluciones IA
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tech">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {techSolutions.map((solution, index) => {
                const IconComponent = iconMap[solution.icon];
                return (
                  <Card
                    key={index}
                    className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-[#00D4D8] bg-white"
                  >
                    <CardHeader>
                      <div className="w-14 h-14 bg-gradient-to-br from-[#00D4D8] to-[#082E5C] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="text-white" size={24} />
                      </div>
                      <CardTitle className="text-lg text-[#082E5C] mb-3">
                        {solution.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-gray-600 mb-4">
                        {solution.description}
                      </CardDescription>
                      {solution.features && (
                        <ul className="space-y-2 mt-4">
                          {solution.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <div className="w-1.5 h-1.5 bg-[#00D4D8] rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-sm text-gray-600">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <CTABanner />
          </TabsContent>

          <TabsContent value="services">
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mb-12">
              {additionalServices.map((service, index) => {
                const IconComponent = iconMap[service.icon];
                return (
                  <Card
                    key={index}
                    className="group hover:shadow-xl transition-all duration-300 border-2 hover:border-[#FFDA0C] bg-white"
                  >
                    <CardHeader>
                      <div className="flex items-start space-x-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-[#FFDA0C] to-[#082E5C] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-all">
                          <IconComponent className="text-white" size={24} />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-lg text-[#082E5C] mb-3">
                            {service.title}
                          </CardTitle>
                          <CardDescription className="text-base text-gray-600 leading-relaxed">
                            {service.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                );
              })}
            </div>
            <CTABanner />
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

export default TechSolutions;
